import topicsData from "./data/topics.json";
import dependenciesData from "./data/dependencies.json";
import clustersData from "./data/clusters.json";
import manifestData from "./data/manifest.json";

export type Topic = {
  id: string;
  type: string;
  subject: string;
  domain: string;
  name: string;
  description: string;
  ageRangeStart: number;
  ageRangeEnd: number;
  centrality: number;
  evidence: string[];
  assessmentPrompt?: string;
  standards: string[];
};

export type Dependency = {
  topicId: string;
  prerequisiteId: string;
  strength: "hard" | "soft";
  reason: string;
};

export type Cluster = {
  subject: string;
  domain: string;
  ageRangeStart: number;
  summary: string;
};

export type ProgressState = "not-started" | "learning" | "mastered";

export const topics = (topicsData as { topics: Topic[] }).topics;
export const dependencies = (
  dependenciesData as { dependencies: Dependency[] }
).dependencies;
export const clusters = (clustersData as { clusters: Cluster[] }).clusters;
export const manifest = manifestData as {
  dataset: string;
  taxonomyVersion: string;
  generatedAt: string;
  counts: {
    topics: number;
    dependencies: number;
    clusters: number;
    curricula: number;
  };
};

export const byId = new Map(topics.map((topic) => [topic.id, topic]));

const incoming = new Map<string, Dependency[]>();
const outgoing = new Map<string, Dependency[]>();

for (const dependency of dependencies) {
  incoming.set(dependency.topicId, [
    ...(incoming.get(dependency.topicId) ?? []),
    dependency
  ]);
  outgoing.set(dependency.prerequisiteId, [
    ...(outgoing.get(dependency.prerequisiteId) ?? []),
    dependency
  ]);
}

export const subjects = [...new Set(topics.map((topic) => topic.subject))].sort();

export function domainsForSubject(subject: string) {
  return [
    ...new Set(
      topics
        .filter((topic) => subject === "All" || topic.subject === subject)
        .map((topic) => topic.domain)
    )
  ].sort();
}

export function cleanText(value: string) {
  return value.replaceAll("??", "-").replace(/\s+/g, " ").trim();
}

export function getPrerequisites(topicId: string) {
  return (incoming.get(topicId) ?? [])
    .map((dependency) => ({
      dependency,
      topic: byId.get(dependency.prerequisiteId)
    }))
    .filter((item): item is { dependency: Dependency; topic: Topic } =>
      Boolean(item.topic)
    )
    .sort(compareTopicLinks);
}

export function getUnlocks(topicId: string) {
  return (outgoing.get(topicId) ?? [])
    .map((dependency) => ({
      dependency,
      topic: byId.get(dependency.topicId)
    }))
    .filter((item): item is { dependency: Dependency; topic: Topic } =>
      Boolean(item.topic)
    )
    .sort(compareTopicLinks);
}

export function findCluster(topic: Topic) {
  return clusters.find(
    (cluster) =>
      cluster.subject === topic.subject &&
      cluster.domain === topic.domain &&
      cluster.ageRangeStart === topic.ageRangeStart
  );
}

export function suggestTopics(progress: Record<string, ProgressState>) {
  const mastered = new Set(
    Object.entries(progress)
      .filter(([, state]) => state === "mastered")
      .map(([topicId]) => topicId)
  );

  return topics
    .filter((topic) => !mastered.has(topic.id))
    .map((topic) => {
      const prereqs = getPrerequisites(topic.id);
      const hardPrereqs = prereqs.filter(
        ({ dependency }) => dependency.strength === "hard"
      );
      const masteredPrereqs = hardPrereqs.filter(({ topic: prerequisite }) =>
        mastered.has(prerequisite.id)
      ).length;

      return {
        topic,
        score:
          masteredPrereqs * 4 -
          hardPrereqs.length +
          topic.centrality * 10 -
          topic.ageRangeStart * 0.1,
        masteredPrereqs,
        totalHardPrereqs: hardPrereqs.length
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
}

function compareTopicLinks(
  a: { dependency: Dependency; topic: Topic },
  b: { dependency: Dependency; topic: Topic }
) {
  if (a.dependency.strength !== b.dependency.strength) {
    return a.dependency.strength === "hard" ? -1 : 1;
  }
  return a.topic.ageRangeStart - b.topic.ageRangeStart || a.topic.name.localeCompare(b.topic.name);
}
